import { basePath } from "@Common/constants";
import { ERROR_CODES, type ErrorCode } from "@Common/error_codes";
import {
	type ResourceNamesStandard,
	resourcesNamesStandard,
} from "@Common/types/resources";
import type { ErrorType } from "@Src/constants";
import { resourcesStandard } from "@Src/resources/resources_config";
import simpleRestProvider from "ra-data-simple-rest";
import {
	type CreateParams,
	type DataProvider,
	type DeleteManyParams,
	type DeleteParams,
	fetchUtils,
	type GetListParams,
	type GetManyParams,
	type GetManyReferenceParams,
	type GetOneParams,
	HttpError,
	type UpdateManyParams,
	type UpdateParams,
} from "react-admin";
import { match } from "ts-pattern";

export const getApiOrigin = (): string => window.origin;
const apiUrl = `${getApiOrigin()}/apibo`;

const resourceTypeguard = (resource: string) => {
	if (resourcesNamesStandard.includes(resource as ResourceNamesStandard)) {
		return resource as ResourceNamesStandard;
	}
	throw Error(`Unknown resource: ${resource}`);
};

const swapResource = (resource: ResourceNamesStandard) => {
	const resourceStandardTrial = resourcesStandard[resource];
	if (resourceStandardTrial) {
		return resourceStandardTrial.resource.apiPath;
	}
	throw Error(`Unknown Resource: ${resource}`);
};

const httpClient = async (url: string, options: fetchUtils.Options = {}) => {
	if (!options.headers) {
		options.headers = new Headers({ Accept: "application/json" });
	}
	const response = await fetchUtils.fetchJson(apiUrl + url, {
		...options,
		credentials: "include", // Include cookies for better-auth session
	});

	return response;
};

const dataProvider = simpleRestProvider("", httpClient);

const redirectToErrorPage = (errorMessage?: string) => {
	const params = new URLSearchParams();
	if (errorMessage) params.set("message", errorMessage);
	const qs = params.toString();

	// HashRouter-compatible navigation
	window.location.replace(`${basePath}/error${qs ? `?${qs}` : ""}`);
};

export const handlerError = (error: ErrorType) => {
	// Zod validation errors from zod-express-middleware return a 400 with an array body.
	// Transform them into the format react-admin expects for per-field errors.
	if (error.status === 400 && Array.isArray(error.body)) {
		const bodyEntry = error.body.find(
			(e: { type: string }) => e.type === "Body",
		);
		if (bodyEntry?.errors?.name === "ZodError") {
			try {
				const issues = JSON.parse(bodyEntry.errors.message) as {
					path: string[];
					message: string;
				}[];
				const fieldErrors: Record<string, string> = {};
				for (const issue of issues) {
					const field = issue.path.join(".");
					if (field && !fieldErrors[field]) {
						fieldErrors[field] = issue.message;
					}
				}
				if (Object.keys(fieldErrors).length > 0) {
					return Promise.reject(
						new HttpError("Validation error", 400, {
							errors: fieldErrors,
						}),
					);
				}
			} catch (err) {
				// JSON parse failed — fall through to default handling
				console.log(err);
			}
		}
		return Promise.reject(error);
	}

	// @ts-expect-error
	const errorMessage = error.body.error.message as ErrorCode;
	// We use match/exhaustive here to benefit from typecheck,
	// and try/catch so that 500 errors still yields Promise.reject(error)
	// instead of match error
	try {
		match(errorMessage)
			.with(ERROR_CODES.SUBSCRIPTION_APPLICATION_SUBMISSION_LIMIT, () =>
				redirectToErrorPage(errorMessage),
			)
			.with(ERROR_CODES.SUBSCRIPTION_FORM_CREATION_LIMIT, () =>
				redirectToErrorPage(errorMessage),
			)
			.with(ERROR_CODES.LINK_INVALID, () => redirectToErrorPage(errorMessage))
			// NOTE: this could also have been adressed through FormRedirectionWrapper
			// but both approaches are valid so I did not refactor
			.with(ERROR_CODES.LINK_ALREADY_USED, () =>
				redirectToErrorPage(errorMessage),
			)
			.with(ERROR_CODES.MISSING_LINKEDIN_INFO, () =>
				redirectToErrorPage(errorMessage),
			)
			.with(ERROR_CODES.LINKEDIN_EMAIL_ALREADY_TAKEN, () =>
				redirectToErrorPage(errorMessage),
			)
			.with(ERROR_CODES.EMAIL_DOMAIN_NOT_ALLOWED, () =>
				redirectToErrorPage(errorMessage),
			)
			.exhaustive();
	} catch (_err) {
		return Promise.reject(error);
	}
	return Promise.reject(error);
};

const customDataProvider: DataProvider = {
	getList: (resource: string, params: Readonly<GetListParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.getList(resourceSwapped, params);
	},
	getOne: (resource: string, params: Readonly<GetOneParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.getOne(resourceSwapped, params).catch((error) => {
			return handlerError(error);
		});
	},
	getMany: (resource: string, params: Readonly<GetManyParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.getMany(resourceSwapped, params).catch((error) => {
			return handlerError(error);
		});
	},
	getManyReference: (
		resource: string,
		params: Readonly<GetManyReferenceParams>,
	) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider
			.getManyReference(resourceSwapped, params)
			.catch((error) => {
				return handlerError(error);
			});
	},
	update: (resource: string, params: Readonly<UpdateParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.update(resourceSwapped, params).catch((error) => {
			return handlerError(error);
		});
	},
	updateMany: (resource: string, params: Readonly<UpdateManyParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.updateMany(resourceSwapped, params).catch((error) => {
			return handlerError(error);
		});
	},
	create: (resource: string, params: Readonly<CreateParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.create(resourceSwapped, params).catch((error) => {
			return handlerError(error);
		});
	},
	delete: (resource: string, params: Readonly<DeleteParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.delete(resourceSwapped, params).catch((error) => {
			return handlerError(error);
		});
	},
	deleteMany: (resource: string, params: Readonly<DeleteManyParams>) => {
		const resourceTyped = resourceTypeguard(resource);
		const resourceSwapped = swapResource(resourceTyped);
		return dataProvider.deleteMany(resourceSwapped, params).catch((error) => {
			return handlerError(error);
		});
	},
};

export { customDataProvider, httpClient };

export default customDataProvider;
